// Keeps consultation voice recordings on the device (IndexedDB) until the
// prescription is saved, so a failed transcription can be retried and a
// closed/reloaded page does not lose the audio. One recording per key
// (the appointment ID). Cleared on logout.

const DB_NAME = 'swasthVoiceDrafts';
const STORE = 'recordings';

export type VoiceDraftStatus = 'pending' | 'failed' | 'done';

export interface VoiceDraft {
  key: string;
  audio: ArrayBuffer;     // stored as ArrayBuffer: Blob storage is unreliable in some iOS webviews
  mimeType: string;
  durationMs?: number;
  savedAt: string;        // ISO
  status: VoiceDraftStatus;
}

export class VoiceDraftStore {

  private static open(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        reject('IndexedDB not available');
        return;
      }
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: 'key' });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  private static async run<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest | void): Promise<T> {
    const db = await this.open();
    return new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const req = fn(tx.objectStore(STORE));
      tx.oncomplete = () => {
        db.close();
        resolve(req ? (req as IDBRequest).result : undefined);
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error);
      };
    });
  }

  static async save(key: any, blob: Blob, mimeType: string, durationMs?: number): Promise<boolean> {
    if (key == null || key === '') {
      return false;
    }
    try {
      const audio = await new Promise<ArrayBuffer>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as ArrayBuffer);
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(blob);
      });
      const draft: VoiceDraft = {
        key: String(key), audio, mimeType, durationMs,
        savedAt: new Date().toISOString(), status: 'pending'
      };
      await this.run('readwrite', s => s.put(draft));
      return true;
    } catch (e) {
      console.log('Voice draft save failed', e);
      return false;
    }
  }

  static async load(key: any): Promise<VoiceDraft | null> {
    if (key == null || key === '') {
      return null;
    }
    try {
      const d = await this.run<VoiceDraft>('readonly', s => s.get(String(key)));
      return d || null;
    } catch (e) {
      console.log('Voice draft load failed', e);
      return null;
    }
  }

  static toBlob(d: VoiceDraft): Blob {
    return new Blob([d.audio], { type: d.mimeType });
  }

  static async setStatus(key: any, status: VoiceDraftStatus): Promise<void> {
    const d = await this.load(key);
    if (!d) {
      return;
    }
    d.status = status;
    try {
      await this.run('readwrite', s => s.put(d));
    } catch (e) {
      console.log('Voice draft status update failed', e);
    }
  }

  static async remove(key: any): Promise<void> {
    if (key == null || key === '') {
      return;
    }
    try {
      await this.run('readwrite', s => s.delete(String(key)));
    } catch (e) {
      console.log('Voice draft remove failed', e);
    }
  }

  // Called on logout.
  static async clearAll(): Promise<void> {
    try {
      await this.run('readwrite', s => s.clear());
    } catch (e) {
      console.log('Voice draft clear failed', e);
    }
  }
}
